import { z } from 'zod';

// Legacy product categories that a recommendation setting can weight.
// `Other` is the required default bucket; the rest are optional overrides.
export const recommendationCategories = [
  'Accommodation',
  'Destination',
  'Activity',
  'Attraction',
  'Transportation',
  'Venue',
  'Dining',
  'Other',
] as const;

export const recommendationCategorySchema = z.enum(recommendationCategories);
export type RecommendationCategory = z.infer<
  typeof recommendationCategorySchema
>;

// Per-category scoring weights (percentages). Stored in the `setting` JSONB.
export const filterWeightsSchema = z.object({
  geolocation: z.number().min(0),
  price: z.number().min(0),
  rating: z.number().min(0),
  boost: z.number().min(0),
});

export type FilterWeights = z.infer<typeof filterWeightsSchema>;

// category -> weights map. Open record (string keys) to tolerate legacy rows
// that may carry categories outside the canonical list.
export const recommendationSettingConfigSchema = z.record(
  z.string(),
  filterWeightsSchema
);

export type RecommendationSettingConfig = z.infer<
  typeof recommendationSettingConfigSchema
>;

export const recommendationSettingSchema = z.object({
  id: z.number().int(),
  title: z.string().nullable(),
  active: z.boolean().nullable(),
  createdOn: z.string().nullable(),
  setting: recommendationSettingConfigSchema,
});

export type RecommendationSetting = z.infer<typeof recommendationSettingSchema>;

export const createRecommendationSettingInput = z.object({
  title: z.string().max(200).optional(),
  setting: recommendationSettingConfigSchema,
});

export type CreateRecommendationSettingInput = z.infer<
  typeof createRecommendationSettingInput
>;

export const updateRecommendationSettingInput = z.object({
  id: z.number().int(),
  title: z.string().max(200).nullable().optional(),
  setting: recommendationSettingConfigSchema.optional(),
});

export type UpdateRecommendationSettingInput = z.infer<
  typeof updateRecommendationSettingInput
>;

export const setActiveRecommendationSettingInput = z.object({
  id: z.number().int(),
});

export type SetActiveRecommendationSettingInput = z.infer<
  typeof setActiveRecommendationSettingInput
>;

export const deleteRecommendationSettingInput = z.object({
  id: z.number().int(),
});

export type DeleteRecommendationSettingInput = z.infer<
  typeof deleteRecommendationSettingInput
>;

export const findRecommendationSettingsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
});

export type FindRecommendationSettingsInput = z.infer<
  typeof findRecommendationSettingsInput
>;

export const findRecommendationSettingsResultSchema = z.object({
  data: z.array(recommendationSettingSchema),
  count: z.number().int().nonnegative(),
});

export type FindRecommendationSettingsResult = z.infer<
  typeof findRecommendationSettingsResultSchema
>;
